/**
 * @deprecated This composable is being phased out in favor of the new feature-based structure.
 * For type imports, use: @accounts/composables/api/mapbox/useMapboxAccountsApi
 * For account data and operations, use the new @accounts/composables modules.
 */

import type { AccountsApiResponse } from '@/components/account/types'
import type { TableFilter, TableRow } from '@/components/table/types'
import type { AccountsMapMarker } from '@/composables/useAccountsMapboxLegacy'
import { useAuth } from '@/composables/useAuth'
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useTags } from '@/composables/useTags'
import { useZones } from '@/composables/useZones'
import { API_BASE } from '@/constants/index.js'
import {
	mapAccountsTable, mapServiceLocationFeatures, mapTagsToFilterOptions, mapZonesToNeighborhoodOptions
} from '@/utils/mappers/AccountsTableMap'
import { useAccountsBatchActions } from '@accounts/composables'
import type {
	AccountRequestBody,
	MapboxServiceLocationFeature,
	PortalMapAccountListResponse,
	ServiceLocationProperties
} from '@accounts/types'
import {
	mapAccountsMarkers as mapAccountsMarkersCore,
	mapToLegacyAccountsMarkers
} from '@accounts/utils'
import {
	computed,
	onMounted,
	ref
} from 'vue'

const tableData = ref<TableRow[]>([])
const totalItems = ref(0)
const loading = ref(true)
const timeLastFetched = ref(0)
const requestBody = ref<AccountRequestBody>({
	page: 1,
	limit: null,
	sortColumn: 'id',
	sortOrder: 'ASC',
	searchValues: {}
})
// used to store the last fetched request body before lassoing
// to be able to refetch the same data if lassoing is canceled.
const storedRequestBody = ref<AccountRequestBody>({
	page: 1,
	limit: null,
	sortColumn: 'id',
	sortOrder: 'ASC',
	searchValues: {}
})

const customers = ref<ServiceLocationProperties[]>([])
const mapboxMarkers = ref<AccountsMapMarker[]>([])

const showTableMarkers = ref(false)

const limit = computed(() => requestBody.value.limit === null ? totalItems.value : null)

export const useAccounts = () => {
	const { getZones, zones } = useZones()
	const { bugsnagReport } = useErrorReporter()
	const { userData } = useAuth()
	const {
		getTags, tags, tableTags, loading: tagsLoading
	} = useTags()

	// Use shared batch actions composable
	const {
		performBatchAction,
		clearBatchActionState,
		batchActionLoading,
		batchActionSuccess,
		batchActionError,
		batchActionErrorMessage,
		skippedAccounts
	} = useAccountsBatchActions()

	if (!tableTags.value?.length) {
		onMounted(() => {
			getTags()
		})
	}

	if (!zones.value?.length) {
		getZones()
	}

	const neighborhoodOptions = computed<TableFilter[]>(() => mapZonesToNeighborhoodOptions(zones.value) || [])
	const tagOptions = computed<TableFilter[]>(() => mapTagsToFilterOptions(tags.value, tableTags.value) || [])

	const refetchAccounts = async (data?: { body?: AccountRequestBody
		store?: boolean
		restore?: boolean }) => {
		const {
			body, store, restore
		} = data || {}

		await fetchAccounts({
			body,
			refetch: true,
			store,
			restore
		})
	}

	const fetchAccounts = async (data?: { body?: AccountRequestBody
		store?: boolean
		restore?: boolean
		refetch?: boolean }) => {
		const {
			body, store, restore, refetch
		} = data || {}

		if (!userData.value) {
			return
		} // Don't fetch if user data is not available

		// If we're restoring, override requestBody with the stored version.
		if (restore) {
			requestBody.value = storedRequestBody.value
		} else if (body) {
			// If we need to store the current requestBody before updating (e.g. for lasso actions)
			if (store && !storedRequestBody.value) { // Don't overwrite the stored request if it exists to prevent further lassoing from overwriting it.
				storedRequestBody.value = requestBody.value
			}
			requestBody.value = body
		}

		// If not a refetch and data exists recently, skip fetching.
		const nextFetchTime = timeLastFetched.value + 10 * 60000
		if (!refetch && tableData.value?.length && Date.now() < nextFetchTime) {
			return
		}

		timeLastFetched.value = Date.now()
		loading.value = true
		await $fetch(`${API_BASE}/portal/accounts`, {
			method: 'POST',
			credentials: 'include',
			server: false,
			body: requestBody.value,
			onResponse({ response }) {
				if (!response?.ok) {
					bugsnagReport({
						error: response._data?.error || response._data?.errors?.[0] || 'Error fetching Accounts data',
						showToast: true,
						toast: {
							title: 'Error fetching accounts',
							message: response._data?.error || response._data?.errors?.[0]
						}
					})
				}

				const responseData: AccountsApiResponse[] = response?._data?.accounts
				tableData.value = mapAccountsTable(responseData)
				totalItems.value = response?._data?.total
			}
		}).catch(e => {
			bugsnagReport({
				error: e,
				showToast: true,
				toast: {
					title: 'Error fetching accounts data',
					message: e
				}
			})
		}).finally(() => {
			loading.value = false
		})
	}

	const getAllAccountsForMap = async () => {
		await $fetch(`${API_BASE}/portal/map/accounts/list`, {
			method: 'GET',
			credentials: 'include',
			onResponse({ response }) {
				if (!response?.ok) {
					throw new Error(response._data?.error || response._data?.errors?.[0] || 'Error fetching Accounts data for map')
				}
				const responseData: PortalMapAccountListResponse[] = response?._data
				customers.value = mapServiceLocationFeatures(responseData)
				const features = mapAccountsMarkersCore(customers.value)
				mapboxMarkers.value = mapToLegacyAccountsMarkers(features) || {}
			}
		}).catch(e => {
			bugsnagReport({
				error: e,
				showToast: true,
				toast: {
					title: 'Error fetching accounts data for map',
					message: e
				}
			})
		})
	}

	const movingInThisMonth = (moveInDate: string): boolean => {
		const moveIn = new Date(moveInDate)
		const today = new Date()
		const nextMonth = new Date()
		nextMonth.setDate(today.getDate() + 30)
		const withinNextMonth = moveIn >= today && moveIn <= nextMonth
		return withinNextMonth
	}

	const isPastDue = (moveInDate: string): boolean => {
		const moveIn = new Date(moveInDate)
		const today = new Date()
		return moveIn < today
	}

	return {
		refetchAccounts,
		fetchAccounts,
		getAllAccountsForMap,
		movingInThisMonth,
		isPastDue,
		performBatchAction,
		clearBatchActionState,
		batchActionLoading,
		batchActionSuccess,
		batchActionError,
		batchActionErrorMessage,
		skippedAccounts,
		requestBody,
		storedRequestBody,
		tableData,
		totalItems,
		limit,
		neighborhoodOptions,
		tags,
		tableTags,
		tagOptions,
		tagsLoading,
		loading,
		customers,
		mapboxMarkers,
		showTableMarkers
	}
}
